import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PDV } from '../../AdminClasses';
import { Button, Checkbox, Header, Icon, Input, Table, useFrontier } from 'react-frontier2';
import { formatCommission } from '@arema/components/Util';
import { SetLoading } from '@arema/components/Classes';
import API from '../../API';

interface PdvPaymentMethodsProps{
	pdv_id: number, 
	pdv: PDV,
}

interface PDVMethod{
	type_id: number,
	force_disable: boolean,
	force_priority: boolean,
	commission_percent: string,
	commission_amount: number,
	user_creator: number,
	user_creator_name: string,
	date_created: number,
}

var PdvPaymentMethods = (props: PdvPaymentMethodsProps)=>{
	var { t } = useTranslation();
	var { Toast } = useFrontier();
	var [methods, setMethods] = useState<PDVMethod[]>(null);
	var [loadError, setLoadError] = useState<string>(null);

	useEffect(()=>{
		if(methods!==null) return;
		API.getPdvPaymentMethods(props.pdv_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			for(var i of res.data){
				if(i.commission_percent) i.commission_percent = (i.commission_percent * 100).toString();
			}
			setMethods(res.data);
		}).catch(err=>{
			setLoadError(t('common.unexpected_code', { code: 'LCL-PDVPM-GT' }));
		})
	}, [methods]);

	var onMethodCheckbox = (type_id: number, key: keyof PDVMethod, inverse: boolean=false)=>{
		return (v: boolean)=>{
			var mt = [...methods];
			var mx = mt.findIndex(a=>a.type_id===type_id);
			if(mx===-1) return;
			(mt[mx][key] as any) = inverse ? !v : v;
			setMethods(mt);
		}
	}

	var onMethodChange = (type_id: number, key: keyof PDVMethod)=>{
		return (val: string)=>{
			var mt = [...methods];
			var mx = mt.findIndex(a=>a.type_id===type_id);
			if(mx===-1) return;
			val = val.replace(/[^0-9.]/g, '');
			var v = !val || val.length==0 ? null : (parseFloat(val)<=0 || Number.isNaN(parseFloat(val)) ? null : val);

			(mt[mx][key] as any) = v;
			setMethods(mt);
		}
	}

	var saveMethods = (setLoading: SetLoading)=>{
		setLoading(true);
		API.editPdvPaymentMethods(props.pdv_id, methods.map(a=>({
			type_id: a.type_id,
			force_disable: !!a.force_disable,
			force_priority: !!a.force_priority,
			commission_amount: a.commission_amount && a.commission_amount>0 ? parseFloat(a.commission_amount.toString()) : null,
			commission_percent: a.commission_percent && parseFloat(a.commission_percent)>0 ? (parseFloat(a.commission_percent)/100) : null,
		}))).then(res=>{
			if(res.error) return Toast.error(res.message);
			Toast.success(t('common.config_saved'))
		}).catch(err=>{
			return Toast.error(t('common.unexpected_code', { code: 'LCL-PDVPM-SVMTH' }));
		}).finally(()=>{
			setLoading(false);
		})
	}

	var has_force = useMemo(()=>{
		if(!methods) return false;
		for(var i of methods){
			if(i.force_priority) return true;
		}
		return false;
	}, [methods]);

	if(loadError) return <Header text={t('common.error')} subtext={loadError} />
	if(!methods) return <Header text={t('common.loading')} loading />
	
	return <Table celled 
		style={{ maxWidth: 700, margin: 'auto' }}
	>
		<Table.Head
			title='Formas de pago' 
			centeredIndexes={[0, 4, 5]}
			data={[['Activo', 'Forma de pago', 'Porcentaje', 'Cantidad', 'Comisión', 'Forzar']]}
		/>
		<Table.Body>
			{methods.map(a=>{
				var has_commission = !!a.commission_amount || !!a.commission_percent;
				return <Table.Row key={`PMTH-${a.type_id}`}>
					<Table.Cell collapsing>
						<Checkbox checked={!a.force_disable} onChange={onMethodCheckbox(a.type_id, 'force_disable', true)} />
					</Table.Cell>
					<Table.Cell>
						{t(`payment_method_type.type_${a.type_id}`)}
					</Table.Cell>
					<Table.Cell collapsing>
						<Input onChange={onMethodChange(a.type_id, 'commission_percent')} placeholder='Porcentaje' value={a.commission_percent || ''} style={{ marginBottom: 0, width: 100, opacity: a.force_disable ? 0.4 : 1 }} inputStyle={{ textAlign: 'center'}} />
					</Table.Cell>
					<Table.Cell collapsing>
						<Input onChange={onMethodChange(a.type_id, 'commission_amount')} placeholder='Dinero' value={a.commission_amount || ''} style={{ marginBottom: 0, width: 100, opacity: a.force_disable ? 0.4 : 1 }} inputStyle={{ textAlign: 'center'}} />
					</Table.Cell>
					<Table.Cell collapsing centered style={{ fontWeight: has_commission ? 'bold' : 'normal', color: has_commission ? 'black' : 'gray', minWidth: 130 }}>
						{a.force_disable ? (
							<Icon name='minus' color='gray' />
						) : (
							formatCommission((a.commission_percent ? parseFloat(a.commission_percent) : 0), (a.commission_amount || 0))
						)}
					</Table.Cell>
					<Table.Cell collapsing>
						<Checkbox checked={a.force_priority} onChange={onMethodCheckbox(a.type_id, 'force_priority')} />
					</Table.Cell>
				</Table.Row>
			})}
			{!!has_force && (
				<Table.Cell row colSpan={1000} empty>
					El PDV tiene un método de pago "forzado", lo cual significa que el PDV nunca tomará en cuenta la comisión de esa forma de pago del calendario
				</Table.Cell>
			)}
		</Table.Body>
		<Table.Footer actions={(
			<Button text={t('common.save')} color='black' onClick={saveMethods} />
		)} />
	</Table>
}

export default PdvPaymentMethods;